import { useCallback, useEffect, useState } from "react";
import { ChevronRight, FileDown, Loader2, Lock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp, fmtDate } from "@/lib/payroll";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/lib/useIsMobile";
import { TID } from "@/constants/testIds";
import { useT } from "@/i18n";
import NumField from "@/components/NumField";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BULAN = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli",
  "Agustus", "September", "Oktober", "November", "Desember"];
const FIELD =
  "focus-ring h-11 w-full rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3.5 text-[15px] placeholder:text-[#B0B0B8]";
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ tanggal: today(), nama_barang: "", jumlah: 0, harga_satuan: 0 });

const Header = () => {
  const { t } = useT();
  return (
  <div>
    <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">{t("jarit.title")}</h1>
  </div>
  );
};

export default function JaritPage() {
  const { user } = useAuth();
  const { t, months } = useT();
  const isAdmin = user?.role === "admin" || user?.role === "viewer";
  const readOnly = user?.role === "viewer";
  const now = new Date();
  const [view, setView] = useState("list");
  const [periods, setPeriods] = useState(null);
  const [bulan, setBulan] = useState(now.getMonth() + 1);
  const [tahun, setTahun] = useState(now.getFullYear());
  const [pickBulan, setPickBulan] = useState(now.getMonth() + 1);
  const [pickTahun, setPickTahun] = useState(now.getFullYear());

  const loadPeriods = useCallback(async () => {
    try {
      const { data } = await api.get("/jarit/periods");
      setPeriods(data);
    } catch (e) {
      toast.error(errMsg(e));
      setPeriods([]);
    }
  }, []);

  useEffect(() => {
    if (view === "list") loadPeriods();
  }, [view, loadPeriods]);

  const open = (b, t) => {
    setBulan(b);
    setTahun(t);
    setView("detail");
  };

  if (view === "detail") {
    return <Detail bulan={bulan} tahun={tahun} setBulan={setBulan} setTahun={setTahun}
                   isAdmin={isAdmin} readOnly={readOnly} userName={user?.name} onBack={() => setView("list")} />;
  }

  return (
    <div data-testid={TID.jarit.root} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Header />
        <div className="space-y-1">
          <label className="label block">{t("jarit.openMonth")}</label>
          <div className="flex items-end gap-2">
            <Select value={String(pickBulan)} onValueChange={(v) => setPickBulan(Number(v))}>
              <SelectTrigger data-testid="jarit-pick-bulan" className="h-10 w-32 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3 text-[14px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <SelectItem key={m} value={String(m)}>{months[m - 1]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="number"
              data-testid="jarit-pick-tahun"
              value={pickTahun}
              onChange={(e) => setPickTahun(Number(e.target.value) || pickTahun)}
              className="h-10 w-20 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3 text-[14px]"
            />
            <button
              data-testid={TID.jarit.openCurrent}
              onClick={() => open(pickBulan, pickTahun)}
              className="flex h-10 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98]"
            >
              <Plus className="h-4 w-4" /> {t("jarit.open")}
            </button>
          </div>
        </div>
      </div>

      {periods === null ? (
        <div className="grid place-items-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" />
        </div>
      ) : periods.length === 0 ? (
        <div className="card-soft px-6 py-20 text-center text-[14px] text-[#8E8E93]">
          {t("jarit.emptyPeriods")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {periods.map((p) => (
            <button
              key={`${p.tahun}-${p.bulan}`}
              data-testid={TID.jarit.periodOpen(p.tahun, p.bulan)}
              onClick={() => open(p.bulan, p.tahun)}
              className="card-soft group flex flex-col items-start gap-3 p-5 text-left transition-all hover:shadow-md"
            >
              <div className="flex w-full items-center justify-between">
                <span className="text-[17px] font-semibold tracking-tight">{p.label}</span>
                <ChevronRight className="h-4 w-4 text-[#B0B0B8] transition-transform group-hover:translate-x-0.5" />
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-[#8E8E93]">
                <span>{t("jarit.tailorCount", { n: p.tailor_count })}</span>
                <span>·</span>
                <span>{t("jarit.entryCount", { n: p.entry_count })}</span>
              </div>
              <div>
                <p className="label">{t("jarit.totalCost")}</p>
                <p className="num text-[18px] font-bold text-[#0071E3]">Rp {rp(p.total_potong_bon)}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Detail({ bulan, tahun, setBulan, setTahun, isAdmin, readOnly, userName, onBack }) {
  const { t, months } = useT();
  const now = new Date();
  const [tailors, setTailors] = useState([]);
  const [sel, setSel] = useState("");
  const [entries, setEntries] = useState([]);
  const [recap, setRecap] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [rc, setRc] = useState({ bpjs: false, kasbon: 0, bonus: 0 });
  const [busy, setBusy] = useState(false);
  const [lock, setLock] = useState({});
  const locked = !!lock.locked;
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get("/jarit/tailors")
      .then(({ data }) => setTailors(data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const loadMonth = useCallback(async () => {
    try {
      const [r, lk] = await Promise.all([
        api.get("/jarit/recap", { params: { bulan, tahun } }),
        api.get("/jarit/lock", { params: { bulan, tahun } }),
      ]);
      setRecap(r.data);
      setLock(lk.data);
    } catch (err) { toast.error(errMsg(err)); }
  }, [bulan, tahun]);

  const loadEntries = useCallback(async () => {
    if (!sel) { setEntries([]); return; }
    try {
      const e = await api.get("/jarit/entries", { params: { bulan, tahun, employee_id: sel } });
      setEntries(e.data);
    } catch (err) { toast.error(errMsg(err)); }
  }, [sel, bulan, tahun]);

  const load = useCallback(async () => {
    await Promise.all([loadMonth(), loadEntries()]);
  }, [loadMonth, loadEntries]);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const myRecap = recap.find((r) => r.employee_id === sel) ||
    { total_potong_bon: 0, bpjs: 0, bonus: 0 };

  useEffect(() => {
    setRc({ bpjs: !!myRecap.bpjs, kasbon: myRecap.kasbon || 0, bonus: myRecap.bonus || 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, myRecap.bpjs, myRecap.bonus, myRecap.kasbon]);

  const preview = Math.round((form.jumlah || 0) * (form.harga_satuan || 0));
  const bpjsAmt = rc.bpjs ? 45000 : 0;
  const totalSisa = (myRecap.total_potong_bon || 0) - bpjsAmt - (rc.kasbon || 0) + (rc.bonus || 0);
  const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pad2 = (n) => String(n).padStart(2, "0");
  const monthStart = `${tahun}-${pad2(bulan)}-01`;
  const monthEnd = `${tahun}-${pad2(bulan)}-${pad2(new Date(tahun, bulan, 0).getDate())}`;
  const clampDate = (d) => (!d ? monthStart : d < monthStart ? monthStart : d > monthEnd ? monthEnd : d);

  useEffect(() => {
    setForm((f) => ({ ...f, tanggal: clampDate(f.tanggal) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulan, tahun]);

  const requestEdit = async () => {
    if (!lock.period_id) return toast.error(t("jarit.periodNone"));
    try {
      await api.post(`/periods/${lock.period_id}/request-edit`);
      setLock((l) => ({ ...l, edit_requested: true }));
      toast.success(t("jarit.editReqSent"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const add = async () => {
    if (!sel) return toast.error(t("jarit.pickTailorErr"));
    if (!form.nama_barang.trim()) return toast.error(t("jarit.fillBarang"));
    if (!(form.jumlah > 0) || !(form.harga_satuan > 0))
      return toast.error(t("jarit.qtyPriceErr"));
    setBusy(true);
    try {
      await api.post("/jarit/entries", { employee_id: sel, ...form });
      setForm((f) => ({ ...emptyForm(), tanggal: f.tanggal }));
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/jarit/entries/${id}`); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveRecap = async () => {
    try {
      await api.put("/jarit/recap", { employee_id: sel, bulan, tahun, bpjs: rc.bpjs, kasbon: rc.kasbon, bonus: rc.bonus });
      toast.success(t("jarit.recapSaved"));
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const selName = tailors.find((t) => t.id === sel)?.nama || "";

  const blobDL = (res, fallback) => {
    const disp = res.headers["content-disposition"] || "";
    const m = disp.match(/filename="?([^"]+)"?/);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = m ? m[1] : fallback;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const exportXlsx = async (employeeId) => {
    try {
      const params = { bulan, tahun, ...(employeeId ? { employee_id: employeeId } : {}) };
      const res = await api.get("/jarit/xlsx", { params, responseType: "blob" });
      blobDL(res, "Rekap-Jarit.xlsx");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const downloadSlip = async (employeeId) => {
    try {
      const params = { bulan, tahun, ...(employeeId ? { employee_id: employeeId } : {}) };
      const res = await api.get("/jarit/slip", { params, responseType: "blob" });
      blobDL(res, "Slip-Jarit.pdf");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={TID.jarit.root} className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          data-testid={TID.jarit.back}
          onClick={onBack}
          className="text-[13.5px] font-medium text-[#6E6E73] transition-colors hover:text-[#1D1D1F]"
        >
          ← {t("jarit.allMonths")}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={TID.jarit.exportMenu}
              className="flex h-10 items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white px-4 text-[14px] font-medium text-[#1D1D1F] transition-all hover:bg-[#F2F2F7] active:scale-[0.98]"
            >
              <FileDown className="h-4 w-4" /> {t("jarit.exportSlip")}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 rounded-xl">
            <DropdownMenuLabel>{t("jarit.excelRecap")}</DropdownMenuLabel>
            <DropdownMenuItem data-testid={TID.jarit.exportXlsxAll} onClick={() => exportXlsx()}>
              {t("jarit.allTailors")}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid={TID.jarit.exportXlsxOne}
              disabled={!sel}
              onClick={() => exportXlsx(sel)}
            >
              {t("jarit.onlyX", { name: selName || "\u2014" })}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("jarit.slipPdf")}</DropdownMenuLabel>
            <DropdownMenuItem data-testid={TID.jarit.slipAll} onClick={() => downloadSlip()}>
              {t("jarit.allTailors")}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid={TID.jarit.slipOne}
              disabled={!sel}
              onClick={() => downloadSlip(sel)}
            >
              {t("jarit.onlyX", { name: selName || "\u2014" })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Header />

      {locked && (
        <div data-testid="jarit-locked-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#F5D9A8]/70 bg-[#FFF8EC]/70 px-4 py-3 backdrop-blur-xl backdrop-saturate-150">
          <p className="flex items-center gap-2 text-[13.5px] font-medium text-[#8A5A00]">
            <Lock className="h-4 w-4" /> {t("jarit.lockedBanner", { label: `${months[bulan - 1]} ${tahun}` })}
          </p>
          {lock.edit_requested ? (
            <span className="text-[13px] font-medium text-[#8A5A00]">{t("jarit.waitingApproval")}</span>
          ) : readOnly ? null : (
            <button
              data-testid="jarit-request-edit"
              onClick={requestEdit}
              className="flex h-9 items-center gap-1.5 rounded-full bg-[#8A5A00] px-4 text-[13px] font-medium text-white transition-all hover:opacity-90"
            >
              {t("jarit.requestEdit")}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label className="label block">{t("jarit.month")}</label>
          <div data-testid={TID.jarit.bulan}
            className="flex h-11 w-36 items-center rounded-xl border border-[#E1E1E6] bg-[#F2F2F7] px-3.5 text-[15px] font-medium text-[#6E6E73]">
            {months[bulan - 1]}
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="label block">{t("jarit.year")}</label>
          <div data-testid={TID.jarit.tahun}
            className="flex h-11 w-24 items-center rounded-xl border border-[#E1E1E6] bg-[#F2F2F7] px-3.5 text-[15px] font-medium text-[#6E6E73]">
            {tahun}
          </div>
        </div>
      </div>

      {!sel ? (
        <div className="space-y-3">
          <p className="label">{t("jarit.pickTailorTitle")}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {tailors.map((tl) => {
              const r = recap.find((x) => x.employee_id === tl.id) || {};
              return (
                <button key={tl.id} data-testid={`jarit-tailor-card-${tl.id}`} onClick={() => setSel(tl.id)}
                  className="card-soft group flex flex-col items-start gap-3 p-5 text-left transition-all hover:shadow-md active:scale-[0.99]">
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-3">
                      <EmployeeAvatar id={tl.id} nama={tl.nama} hasPhoto={tl.has_photo} size={44} />
                      <span className="truncate text-[16px] font-semibold tracking-tight">{tl.nama}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-[#B0B0B8] transition-transform group-hover:translate-x-0.5" />
                  </div>
                  <div>
                    <p className="label">{t("jarit.totalCost")}</p>
                    <p className="num text-[17px] font-bold text-[#0071E3]">Rp {rp(r.total_potong_bon || 0)}</p>
                  </div>
                </button>
              );
            })}
          </div>
          {tailors.length === 0 && (
            <p className="card-soft px-6 py-16 text-center text-[14px] text-[#8E8E93]">{t("jarit.emptyPeriods")}</p>
          )}
        </div>
      ) : (
        <>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button data-testid="jarit-back-tailors" onClick={() => setSel("")}
            className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#0071E3] hover:underline">
            ← {t("jarit.backTailors")}
          </button>
          <div className="flex items-center gap-2.5">
            <EmployeeAvatar id={sel} nama={selName} hasPhoto={tailors.find((x) => x.id === sel)?.has_photo} size={36} />
            <h2 className="text-[20px] font-semibold tracking-tight">{selName}</h2>
          </div>
        </div>

      {!readOnly && (
      <div className="card-soft space-y-3 p-5">
        <p className="label">{t("jarit.recordToday")}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-[150px_1fr_110px_140px_auto] sm:items-end">
          <div className="space-y-1">
            <label className="label block">{t("jarit.date")}</label>
            <input type="date" data-testid={TID.jarit.tanggal} value={form.tanggal}
              min={monthStart} max={monthEnd}
              onChange={(e) => setF("tanggal")(clampDate(e.target.value))} className={FIELD} />
          </div>
          <div className="col-span-2 space-y-1 sm:col-span-1">
            <label className="label block">{t("jarit.barang")}</label>
            <input data-testid={TID.jarit.barang} value={form.nama_barang}
              onChange={(e) => setF("nama_barang")(e.target.value)}
              placeholder={t("jarit.barangPh")} className={FIELD} />
          </div>
          <div className="space-y-1">
            <label className="label block">{t("jarit.qty")}</label>
            <NumField testid={TID.jarit.jumlah} value={form.jumlah} onChange={setF("jumlah")} className={FIELD} />
          </div>
          <div className="space-y-1">
            <label className="label block">{t("jarit.pricePer")}</label>
            <NumField testid={TID.jarit.harga} value={form.harga_satuan} onChange={setF("harga_satuan")} className={FIELD} />
          </div>
          <button data-testid={TID.jarit.add} onClick={add} disabled={busy || locked}
            className="col-span-2 flex h-11 items-center justify-center gap-1.5 rounded-xl bg-[#0071E3] px-4 text-[15px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.99] disabled:opacity-50 sm:col-span-1">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("jarit.add")}
          </button>
        </div>
        <p className="text-[13px] text-[#8E8E93]">
          {t("jarit.priceTotal")} <span className="num font-semibold text-[#1D1D1F]">Rp {rp(preview)}</span>
        </p>
      </div>
      )}

      {isMobile ? (
        <div className="space-y-2">
          {entries.map((e) => (
            <div key={e.id} className="card-soft flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-[14.5px] font-medium">{e.nama_barang}</p>
                <p className="num text-[12px] text-[#8E8E93]">{fmtDate(e.tanggal)} · {rp(e.jumlah)} × {rp(e.harga_satuan)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="num text-[14px] font-semibold">{rp(e.harga_total)}</span>
                {!readOnly && (
                <button data-testid={TID.jarit.entryDel(e.id)} onClick={() => del(e.id)}
                  className="grid h-8 w-8 place-items-center rounded-full text-[#B0B0B8] transition-colors hover:bg-red-50 hover:text-red-600"
                  aria-label="Delete entry">
                  <Trash2 className="h-4 w-4" />
                </button>
                )}
              </div>
            </div>
          ))}
          {entries.length === 0 && (
            <p className="card-soft px-4 py-14 text-center text-[14px] text-[#8E8E93]">{t("jarit.emptyMonth")}</p>
          )}
        </div>
      ) : (
      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-[13.5px]">
            <thead>
              <tr className="border-b border-black/[0.06] text-[12px] uppercase tracking-wide text-[#8E8E93]">
                <th className="px-4 py-3 text-left">{t("jarit.colDate")}</th>
                <th className="px-4 py-3 text-left">{t("jarit.colBarang")}</th>
                <th className="px-4 py-3 text-right">{t("jarit.colQty")}</th>
                <th className="px-4 py-3 text-right">{t("jarit.colPrice")}</th>
                <th className="px-4 py-3 text-right">{t("jarit.colTotal")}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-black/[0.04]">
                  <td className="num px-4 py-3 text-[#6E6E73]">{fmtDate(e.tanggal)}</td>
                  <td className="px-4 py-3">{e.nama_barang}</td>
                  <td className="num px-4 py-3 text-right">{rp(e.jumlah)}</td>
                  <td className="num px-4 py-3 text-right">{rp(e.harga_satuan)}</td>
                  <td className="num px-4 py-3 text-right font-medium">{rp(e.harga_total)}</td>
                  <td className="px-4 py-3 text-right">
                    {!readOnly && (
                    <button data-testid={TID.jarit.entryDel(e.id)} onClick={() => del(e.id)}
                      className="rounded-lg p-1.5 text-[#B0B0B8] transition-colors hover:bg-red-50 hover:text-red-600"
                      aria-label="Delete entry">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    )}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-14 text-center text-[14px] text-[#8E8E93]">
                    {t("jarit.emptyMonth")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <div className="card-soft space-y-4 p-5">
        <p className="label">{t("jarit.recapTitle", { label: `${months[bulan - 1]} ${tahun}` })}</p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <div>
            <p className="label">{t("jarit.totalCost")}</p>
            <p className="num mt-1 text-[17px] font-semibold">Rp {rp(myRecap.total_potong_bon || 0)}</p>
          </div>
          <div>
            <p className="label">BPJS</p>
            <div className="mt-2 flex items-center gap-2">
              <Switch data-testid={TID.jarit.bpjs} checked={!!rc.bpjs} disabled={readOnly}
                onCheckedChange={(v) => setRc((s) => ({ ...s, bpjs: v }))} />
              <span className="text-[14px] text-[#6E6E73]">{rc.bpjs ? "Rp 45.000" : t("jarit.bpjsNo")}</span>
            </div>
          </div>
          <div>
            <p className="label">{t("jarit.kasbon")}</p>
            <NumField testid={TID.jarit.kasbon} value={rc.kasbon} disabled={readOnly}
              onChange={(v) => setRc((s) => ({ ...s, kasbon: v }))} align="text-left" className={`${FIELD} mt-1`} />
          </div>
          <div>
            <p className="label">{t("jarit.bonus")}</p>
            <NumField testid={TID.jarit.bonus} value={rc.bonus} disabled={readOnly}
              onChange={(v) => setRc((s) => ({ ...s, bonus: v }))} align="text-left" className={`${FIELD} mt-1`} />
          </div>
          <div>
            <p className="label">{t("jarit.totalRemain")}</p>
            <p data-testid={TID.jarit.totalSisa} className="num mt-1 text-[17px] font-bold text-[#0071E3]">
              Rp {rp(totalSisa)}
            </p>
          </div>
        </div>
        {!locked && !readOnly && (
        <button data-testid={TID.jarit.saveRecap} onClick={saveRecap} disabled={locked}
          className="h-10 rounded-full bg-[#0071E3] px-5 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50">
          {t("jarit.saveRecap")}
        </button>
        )}
      </div>
        </>
      )}
    </div>
  );
}
